import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';

import { BehaviorSubject, Subscription, fromEvent } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { cloneDeep, get } from 'lodash';

import { Polyline, Marker, LatLng, DivIcon, CircleMarker } from 'leaflet';
import * as L from 'leaflet';
import '../../../../../../assets/plugins/leaflet.polylineDecorator.js';

import { RouteEvent } from '../../../../models/domain/RouteEvent';
import { Point } from '../../../../models/domain/Point';
import { MapService } from '../../map/map.service';
import { VehicleLocation } from '../../../../models/domain/VehicleLocation';
import { RouteEventPoint } from '../../../../models/domain/RouteEventPoint';
import { RouteElementTypeEnum } from '../../../../enums/route-element-type.enum';
import { Consts } from '../../../../consts/consts';

interface IRouteSubject {
    route?: RouteEvent[];
    selectedSegment?: RouteEvent;
    ifSelectedMoreThen2DaysRange?: boolean;
}

/**
 * Provides route drawing utilities
 */
@Injectable()
export class RouteDrawerService implements OnDestroy {

    public routeData$: BehaviorSubject<IRouteSubject> = new BehaviorSubject(null as any);
    private onChangeRouteDataSubsc: Subscription;

    /**
     * Subscription to drawing route events
     */
    private preventFitBounds?: boolean;
    private routeEvents?: RouteEvent[];
    private selectedRoutePointsMarker: CircleMarker[] = [];
    private listenToMapEvents: boolean;
    private arrowDecorators: L.Layer[] = [];

    /**
     * Service constructor
     * @param $map map service
     */
    public constructor(
        private $map: MapService,
        private ngZone: NgZone,
        private datePipe: DatePipe,
    ) {
        this.onChangeRouteDataSubsc = this.routeData$
        .pipe(
            filter((value) => {
              if (value) {
                  return true;
              }
              this.removeAllRouteMarkersDraw(undefined);
              return false;
            }),
            tap((value) => {
                if (!value?.selectedSegment && value?.route?.length) {
                  this.$map.setMapLoaderState(true);
                }
            }),
        )
        .subscribe({
            next: (data) => {
              this.drawRouteEvents(data.route!, data.selectedSegment!);
              this.onClickSegmentRoute(data);
            },
        });
    }

    ngOnDestroy(): void {
        this.onChangeRouteDataSubsc?.unsubscribe();
    }

    private get currentValueRouteData(): IRouteSubject {
        return cloneDeep(get(this.routeData$, 'value', {}));
    }

    public onClickPartOfRoute(data: RouteEvent | undefined): void {
        const currentValueRouteData = this.currentValueRouteData;
        this.routeData$.next({
            ...currentValueRouteData,
            selectedSegment: cloneDeep(data),
        });
    }

    public setRoute(data: RouteEvent[] | undefined, ifSelectedMoreThen2DaysRange?: boolean): void {
        this.routeData$.next({
            selectedSegment: undefined,
            route: cloneDeep(data),
            ifSelectedMoreThen2DaysRange,
        });
    }

    private onClickSegmentRoute({route, selectedSegment}: IRouteSubject): void {
      if (selectedSegment) {
        if (this.ifShowModalPark(route!, selectedSegment)) {
            const data = selectedSegment?.T === 1 ? selectedSegment : null;
            this.$map.onClickParkStopIcon$.next(data);
        }
      }
    }

    private ifShowModalPark(routes: RouteEvent[], selectedRoute: RouteEvent): boolean {
        const [first] = routes;
        const lastElementRoute = routes[routes.length - 1];
        const ifFirstOrLastRouteClicked = first.Id === selectedRoute.Id || lastElementRoute.Id === selectedRoute.Id;
        return ifFirstOrLastRouteClicked ? false : true;
    }

    public resetRoute(): void {
      this.routeData$.next(null!);
    }

    private removeAllRouteMarkersDraw(route?: RouteEvent[]): void {
      this.clearDirectionArrows();
      this.$map.groups.routes.clearLayers();
      this.routeEvents = route;
      this.deleteCurrentDisplayPointsMarker();
    }

    /**
     * Draw route on map
     * @param route route
     * @param selectedSegment route event that user chose to highlight, others should be transparent
     */
    public drawRouteEvents(
        route: RouteEvent[],
        selectedSegment: RouteEvent,
        withOutZoom?: boolean,
    ) {
        this.removeAllRouteMarkersDraw(route);
        let bounds: any;

        if (route) {
            this.listenToMapEvents = true;

            // na koncu zaznaczone
            for (const routeEvent of route) {
                if ((selectedSegment && selectedSegment.Id === routeEvent.Id)) {
                  this.drawRouteEvent(routeEvent, selectedSegment);
                }
            }
            // zbieramy po narysowaniu aktywnych
            bounds = this.$map.groups.routes.getBounds();

            // nie zaznaczone
            for (const routeEvent of route) {
                if ((!selectedSegment || selectedSegment.Id !== routeEvent.Id)) {
                    this.drawRouteEvent(routeEvent, selectedSegment, true);
                }
            }
        }
        else {
            this.listenToMapEvents = false;
            this.preventFitBounds = false;
        }

        if (!withOutZoom && !selectedSegment) {
            setTimeout(() => {
                this.fitBoundsMap(bounds);
            }, 500);
        }

        if (selectedSegment) {
          this.fitBoundsMap(bounds);
          setTimeout(() => {
            this.addPointsToRoute(selectedSegment.Points, true);
            const layer = this.$map.groups.routes.getLayers().find((l: any) => (l instanceof L.Polyline) && ((l as any)._routeEventId === selectedSegment.Id));
            if (layer) {
              this.addDirectionArrows(layer as L.Polyline);
            }
          }, 500);
        }
    }

    private fitBoundsMap(bounds: any): void {
        if (bounds && bounds.isValid() && !this.preventFitBounds) {
            this.$map.map!.fitBounds(bounds, Consts.MapPadding);
        } else {
            bounds = this.$map.groups.routes.getBounds();
            if (bounds && bounds.isValid() && !this.preventFitBounds) {
                this.$map.map!.fitBounds(bounds, Consts.MapPadding);
            }
        }
    }

    private get routePointSizeByZoom(): number {
        // const zoom = this.$map.map?.getZoom() as number;
        // if (zoom === 12) {
        //     return 3;
        // }
        // if (zoom >= 11 && zoom < 13) {
        //     return 2;
        // }
        // if (zoom >= 13 && zoom < 15) {
        //     return 4;
        // }
        // if (zoom >= 15) {
        //     return 5;
        // }
        return 2;
    }

    private addPointsToRoute(points: RouteEventPoint[], isSegmentSelected?: boolean): Promise<void> {
        return new Promise((resolve) => {
            if (typeof Worker !== 'undefined') {
                const worker = new Worker(new URL('../../../../../app.worker', import.meta.url));

                worker.onmessage = ({ data }) => {
                  const { batchMarkers } = data;

                  this.ngZone.runOutsideAngular(() => {
                    const markers = batchMarkers.map(({ latLng, routePointStyle, item }: any) => {
                      const point = L.circleMarker(latLng, routePointStyle);

                      const onCopyMarkerCoords = (): void => {
                          fromEvent<PointerEvent>(document.getElementById('copy-marker-coords') as HTMLElement, 'click').subscribe(
                              (ev) => {
                                  const coords = (ev.target as HTMLElement).attributes.getNamedItem('data-coords')?.value;
                                  navigator.clipboard.writeText(coords as string);
                              }
                          );
                      };

                      const onMouseOutTooltip = (event: MouseEvent): void => {
                          const leafletPopup = document.querySelector('.popup-route-marker');
                          if (leafletPopup) {
                              const rect = leafletPopup.getBoundingClientRect();
                              const ifMouseOutTooltip = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
                              if (ifMouseOutTooltip) {
                                  point.closePopup();
                              }
                          }
                      };

                      const popupContent = `
                        <div class="popup-route-marker__column left">
                          <span class="date">${ this.datePipe.transform(item.T, 'HH:mm:ss d MMMM y') }</span>
                          <span>${ item.Lt }, ${ item.Ln }</span>
                        </div>
                        <div class="popup-route-marker__column right">
                          <a href="https://www.google.com/maps?t=k&q=${ item.Lt }+${ item.Ln }" target="_blank" class="popup-route-marker__btn">
                            <img src="/assets/img/street-view.svg">
                          </a>
                          <button id="copy-marker-coords" data-coords="${ item.Lt }, ${ item.Ln }" class="popup-route-marker__btn">
                            <img src="/assets/img/copy.svg" data-coords="${ item.Lt }, ${ item.Ln }">
                          </button>
                        </div>
                      `;

                      point.bindPopup(popupContent, { className: 'popup-route-marker' });
                      point.on('mouseover', function(this: L.CircleMarker) {
                          this.openPopup();
                          onCopyMarkerCoords();
                      });
                      point.on('mouseout', function(this: L.CircleMarker) {
                          setTimeout(() => {
                              document.addEventListener('mousemove', onMouseOutTooltip);
                          }, 300);
                      });

                      return point;
                    });

                    if (this.$map.map) {
                      L.layerGroup(markers).addTo(this.$map.map);
                    }

                    this.selectedRoutePointsMarker = [...this.selectedRoutePointsMarker, ...markers];
                  });

                  setTimeout(() => {
                    this.$map.setMapLoaderState(false);
                  }, 2000);
                };

                worker.postMessage({
                    points,
                    routePointSizeByZoom: this.routePointSizeByZoom,
                    ifSelectedMoreThen2DaysRange: this.routeData$.value.ifSelectedMoreThen2DaysRange,
                    isSegmentSelected,
                });
              }
              else {
                console.error('Web Workers are not supported in this environment.');
              }
        });
    }

    private deleteCurrentDisplayPointsMarker(): void {
        this.ngZone.runOutsideAngular(() => {
            this.selectedRoutePointsMarker.forEach(item => {
                item.removeEventListener('mouseout');
                item.removeEventListener('mouseover');
                this.$map.map?.removeLayer(item);
            });
            this.selectedRoutePointsMarker = [];
            this.ngZone.run(() => {});
        });
    }

    private addDirectionArrows(polyline: L.Polyline) {
      const decorator = (L as any).polylineDecorator(polyline, {
        patterns: [{
          offset: '10px',
          repeat: '70px',
          symbol: (L as any).Symbol.arrowHead({
            pixelSize: 6, // wielkosc
            headAngle: 60, // dlugosc
            pathOptions: {
              stroke:     true,
              fill:       true,
              color:      'rgba(255, 255, 255, 1)',
              fillColor: 'white',
              weight:     1,
            }
          })
        }]
      });
      decorator.addTo(this.$map.map);
      this.arrowDecorators.push(decorator);
      return decorator;
    }

    public clearDirectionArrows(): void {
      this.arrowDecorators.forEach(dec => {
        this.$map.map?.removeLayer(dec);
      });
      this.arrowDecorators = [];
    }

    private drawRouteEvent(routeEvent: RouteEvent, selectedSegment: RouteEvent, sendBack?: boolean) {
        if (routeEvent.T === RouteElementTypeEnum.Segment || routeEvent.T === RouteElementTypeEnum.UnfinishedSegment) {
            if (routeEvent.Points) {
                const points = routeEvent.Points.map(point => this.convertRoutePointToLatLng(point)).filter(p => p !== null);

                if (points) {
                    const polyline = new Polyline(points as LatLng[], this.definePolyLineOptions(routeEvent, selectedSegment));
                    (polyline as any)._routeEventId = routeEvent.Id;

                    !selectedSegment && setTimeout(() => {
                        this.addPointsToRoute(routeEvent.Points); // dodajep punkty na trasie
                        this.addDirectionArrows(polyline); // dodajemy kierunek jazdy
                    }, 100);

                    this.$map.groups.routes.addLayer(polyline); // dodaje linie trasy

                    if (sendBack === true) {
                        polyline.bringToBack();
                    }
                }
            }
        }
        else if (routeEvent.T === RouteElementTypeEnum.Stop) {
            const latlng = routeEvent.D.S || routeEvent.D.E;
            const point = this.convertVehicleLocationToLatLng(latlng);
            if (!point) {
                return;
            }

            var image = "";

            if (selectedSegment) {
                const selectedIndex = this.routeEvents!.indexOf(selectedSegment);
                var followingEvent = this.routeEvents![selectedIndex - 1];
                var precedingEvent = this.routeEvents![selectedIndex + 1];

                if (routeEvent === followingEvent) {
                    image = '<img src="assets/img/map/routeDestination.png">';
                }
                else if (routeEvent === precedingEvent) {
                    image = '<img src="assets/img/map/routeOrigin.png">';
                }
            }
            else {
                var latestRouteEvent = this.routeEvents![0];
                var oldestRouteEvent = this.routeEvents![this.routeEvents!.length - 1];

                if (routeEvent === latestRouteEvent) {
                    image = '<img src="assets/img/map/routeDestination.png">'; // B icon
                }
                else if (routeEvent === oldestRouteEvent) {
                    image = '<img src="assets/img/map/routeOrigin.png">'; // A icon
                }
            }

            const selected = (!selectedSegment || selectedSegment.Id === routeEvent.Id) ? 'selected' : 'unselected';
            const icon = new DivIcon({ html: `<div class="route-event-point route-event-point-${selected}">${image}</div>` });

            const marker = new Marker(point, {
                'icon': icon,
                'riseOnHover': true
            });

            !image?.length && marker.on('click', () => {
                if (this.ifShowModalPark(this.routeEvents as any[], routeEvent)) {
                    this.$map.onClickParkStopIcon$.next(routeEvent);
                }
            });

            // marker.bindPopup(Description);
            this.$map.groups.routes.addLayer(marker);

            // if (sendBack === true) {
            //     marker.bringToBack();
            // }
        }
    }

    private getPupupHTML(routeEvent: any, point: LatLng) {

        const lat = point.lat.toString().replace(',', '.');
        const lng = point.lng.toString().replace(',', '.');

        const Description2 = routeEvent.D.E || routeEvent.D.S;
        const Description = Description2 ? `${Description2.St}` : '';
        const googleMapsUrl = `https://www.google.com/maps?t=k&q=${lat}+${lng}`;

        return `
        <div class="route-point-popup-content">
        <div class="route-point-popup-label" title="${Description}">${Description}</div>
        <a class="route-point-popup-google-maps-icon" href="${googleMapsUrl}" target="_blank"></a>
        </div>
        `;
    }

    /**
     * Defines how polyline should be displayed according to chosen segment
     * @param routeEvent routeEvent
     * @param selectedSegment route event that user chose to highlight, others should be transparent
     */
    private definePolyLineOptions(routeEvent: RouteEvent, selectedSegment: RouteEvent): any {

        const color = routeEvent.T === 3 ? '#32c80f' : '#2d78ff';

        // Check if user didn't choose any segment, return default values
        if (!selectedSegment) {
            return {
                color: color,
                // color: '#004bd2',
                opacity: 1,
                fillOpacity: 1,
                weight: 7
            };
        }

        const isSelected = (selectedSegment.Id === routeEvent.Id);

        return {
            // color: isSelected ? '#004bd2' : '#969696',
            color: isSelected ? color : '#d7d7d7',
            // fillOpacity: isSelected ? 1 : 0.50,
            opacity: isSelected ? 1 : 0.75,
            weight: 8
        };
    }


    /**
     * Converts given point to Leaflet's LatLng object
     * @param point
     */
    private convertToLatLng(point: Point): LatLng | null {
        if (!point) {
            return null;
        }
        return new LatLng(parseFloat(point.Lat), parseFloat(point.Lon));
    }

    private convertVehicleLocationToLatLng(location: VehicleLocation): LatLng | null {
        if (!location) {
            return null;
        }
        return new LatLng(location.Lt, location.Ln);
    }

    private convertRoutePointToLatLng(point: RouteEventPoint): LatLng | null {
        if (!point) {
            return null;
        }
        return new LatLng(parseFloat(point.Lt.replace(',', '.')), parseFloat(point.Ln.replace(',', '.')));
    }
}
